import {
  del,
  get,
  patch,
  post,
  type RequestConfig,
} from "../shared/api-client";
import type {
  ApiItemResponse,
  Area,
  AreaPayload,
  City,
  CityPayload,
  District,
  DistrictPayload,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  unwrapItem,
} from "./list-api-helpers";

const BASE = JIVANDHARA_BASE;
const SILENT: RequestConfig = { showNotification: false };

class LocationService {
  listDistrictsPage = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<District>> => {
    return fetchListPage<District>(`${BASE}/districts/`, query);
  };

  getDistrict = async (id: number): Promise<District | undefined> => {
    const data = await get<ApiItemResponse<District> | District>(
      `${BASE}/districts/${id}/`,
      SILENT_LIST
    );
    return unwrapItem(data);
  };

  createDistrict = async (
    payload: DistrictPayload
  ): Promise<District | undefined> => {
    const data = await post<
      ApiItemResponse<District> | District,
      DistrictPayload
    >(`${BASE}/districts/`, payload, SILENT);
    return unwrapItem(data);
  };

  updateDistrict = async (
    id: number,
    payload: Partial<DistrictPayload>
  ): Promise<District | undefined> => {
    const data = await patch<
      ApiItemResponse<District> | District,
      Partial<DistrictPayload>
    >(`${BASE}/districts/${id}/`, payload, SILENT);
    return unwrapItem(data);
  };

  deleteDistrict = async (id: number): Promise<void> => {
    await del(`${BASE}/districts/${id}/`, SILENT);
  };

  listCitiesPage = (query: ListQuery = {}): Promise<PaginatedResult<City>> => {
    return fetchListPage<City>(`${BASE}/cities/`, query);
  };

  getCity = async (id: number): Promise<City | undefined> => {
    const data = await get<ApiItemResponse<City> | City>(
      `${BASE}/cities/${id}/`,
      SILENT_LIST
    );
    return unwrapItem(data);
  };

  createCity = async (payload: CityPayload): Promise<City | undefined> => {
    const data = await post<ApiItemResponse<City> | City, CityPayload>(
      `${BASE}/cities/`,
      payload,
      SILENT
    );
    return unwrapItem(data);
  };

  updateCity = async (
    id: number,
    payload: Partial<CityPayload>
  ): Promise<City | undefined> => {
    const data = await patch<
      ApiItemResponse<City> | City,
      Partial<CityPayload>
    >(`${BASE}/cities/${id}/`, payload, SILENT);
    return unwrapItem(data);
  };

  deleteCity = async (id: number): Promise<void> => {
    await del(`${BASE}/cities/${id}/`, SILENT);
  };

  listAreasPage = (query: ListQuery = {}): Promise<PaginatedResult<Area>> => {
    return fetchListPage<Area>(`${BASE}/areas/`, query);
  };

  getArea = async (id: number): Promise<Area | undefined> => {
    const data = await get<ApiItemResponse<Area> | Area>(
      `${BASE}/areas/${id}/`,
      SILENT_LIST
    );
    return unwrapItem(data);
  };

  createArea = async (payload: AreaPayload): Promise<Area | undefined> => {
    const data = await post<ApiItemResponse<Area> | Area, AreaPayload>(
      `${BASE}/areas/`,
      payload,
      SILENT
    );
    return unwrapItem(data);
  };

  updateArea = async (
    id: number,
    payload: Partial<AreaPayload>
  ): Promise<Area | undefined> => {
    const data = await patch<
      ApiItemResponse<Area> | Area,
      Partial<AreaPayload>
    >(`${BASE}/areas/${id}/`, payload, SILENT);
    return unwrapItem(data);
  };

  deleteArea = async (id: number): Promise<void> => {
    await del(`${BASE}/areas/${id}/`, SILENT);
  };
}

export const locationService = new LocationService();
